/**
 * SSR 上下文 Hook
 * 提供对 SSR 上下文的访问能力
 *
 * 【为什么自定义？】
 * Vue 官方的 useSSRContext 返回 unknown 类型，且客户端调用会报错
 * 这里提供默认值保护和便捷属性
 */
import { inject, computed } from 'vue';

/**
 * SSR 上下文注入键
 * 使用 Symbol 确保唯一性，避免命名冲突
 */
export const SSR_CONTEXT_KEY = Symbol('ssrContext');

/**
 * 获取 SSR 上下文
 *
 * @returns {object} { initialState, url, isClient }
 *
 * @example
 * const { initialState, isClient } = useSSRContext();
 * const config = initialState?.data?.current?.configInfo;
 */
export function useSSRContext() {
  // 从 provide 注入中获取上下文
  const context = inject(SSR_CONTEXT_KEY);

  // 未找到时返回默认值（防御性编程）
  if (!context) {
    return {
      initialState: { data: {} },
      url: typeof window !== 'undefined' ? window.location.pathname : '/',
      isClient: true,
    };
  }

  return {
    initialState: context.initialState || { data: {} },  // 带默认值保护
    url: context.url || '/',
    isClient: !import.meta.env.SSR,  // Vite 环境变量判断
  };
}

/**
 * 获取当前 host（域名+端口）
 * 自动处理服务端和客户端的差异
 *
 * @returns {import('vue').ComputedRef<string>} 响应式的 host
 */
export function useHost() {
  const { initialState, isClient } = useSSRContext();

  return computed(() => {
    if (isClient) {
      // 客户端：从 window.location 获取
      const { port, hostname } = window.location;
      // HTTPS 默认端口 443 不显示
      return port === '443' || port === '' ? hostname : window.location.host;
    }
    // 服务端：从 initialState 获取
    return initialState.data?.host || '';
  });
}

/**
 * 判断是否为客户端环境
 * @returns {boolean}
 */
export function useIsClient() {
  return !import.meta.env.SSR;
}

/**
 * 判断是否为服务端环境
 * @returns {boolean}
 */
export function useIsServer() {
  return import.meta.env.SSR;
}

export default {
  useSSRContext,
  useHost,
  useIsClient,
  useIsServer,
  SSR_CONTEXT_KEY,
};
